// Laster saldo per lokasjon (ItemBins) for innholds- og fyllgradslaget.
// Lat lasting: henter først når et lag som trenger saldo aktiveres, og caches
// til reload() kalles. Aggregerer per binCode innenfor valgt lager.

import { useCallback, useEffect, useRef, useState } from 'react';
import { itemLocationBalanceService } from '../../../services/itemLocationBalanceService';
import { BinContentSummary } from './types';

export interface BinContentsState {
    /** binCode → aggregert saldo. null = ikke lastet ennå. Lokasjoner uten oppslag = tomme. */
    contents: Map<string, BinContentSummary> | null;
    isLoading: boolean;
    error: string | null;
    reload: () => void;
}

export function useBinContents(enabled: boolean, warehouseID: string): BinContentsState {
    const [contents, setContents] = useState<Map<string, BinContentSummary> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const loadedForWarehouse = useRef<string | null>(null);
    const [reloadCounter, setReloadCounter] = useState(0);

    const reload = useCallback(() => {
        loadedForWarehouse.current = null;
        setReloadCounter(c => c + 1);
    }, []);

    useEffect(() => {
        if (!enabled) return;
        if (loadedForWarehouse.current === warehouseID) return;

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const rows = await itemLocationBalanceService.getAllItemBins();
                if (cancelled) return;

                const map = new Map<string, BinContentSummary>();
                // Distinkte varenumre per bin (samme vare kan ha flere rader pga batch/serie)
                const itemsPerBin = new Map<string, Set<string>>();
                for (const row of rows) {
                    if (String(row.warehouseID) !== String(warehouseID)) continue;
                    if (!row.binCode) continue;

                    const existing = map.get(row.binCode);
                    const summary: BinContentSummary = existing ?? { totalQuantity: 0, itemCount: 0 };
                    summary.totalQuantity += row.quantity ?? 0;
                    if ((row.quantity ?? 0) !== 0 && row.itemNo) {
                        let items = itemsPerBin.get(row.binCode);
                        if (!items) {
                            items = new Set<string>();
                            itemsPerBin.set(row.binCode, items);
                        }
                        items.add(row.itemNo);
                        summary.itemCount = items.size;
                    }
                    if (row.maxQuantity !== null && row.maxQuantity !== undefined) {
                        summary.maxQuantity = (summary.maxQuantity ?? 0) + row.maxQuantity;
                    }
                    if (!existing) map.set(row.binCode, summary);
                }

                loadedForWarehouse.current = warehouseID;
                setContents(map);
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load bin contents');
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [enabled, warehouseID, reloadCounter]);

    return { contents, isLoading, error, reload };
}
